import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ModalHistoricoFornecedor from '../components/ModalHistoricoFornecedor';
import ModalEmailNegociacao from '../components/ModalEmailNegociacao';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

const formatarPct = (v) => {
  if (v === null || v === undefined) return '—';
  const sinal = v > 0 ? '+' : '';
  return `${sinal}${Number(v).toFixed(1)}%`;
};

// Máscara de entrada em formato brasileiro: "25000" -> "25.000", "25000,5" -> "25.000,5"
const formatarInputBRL = (value) => {
  let raw = String(value).replace(/[^\d,]/g, '');
  const parts = raw.split(',');
  let intPart = parts[0].replace(/^0+(?=\d)/, '');
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.length > 1 ? `${intPart},${parts[1].slice(0, 2)}` : intPart;
};

// "25.000,00" -> 25000.00
const parseBRL = (str) => parseFloat((String(str) || '').replace(/\./g, '').replace(',', '.')) || 0;

// 25000 -> "25.000,00" (sem o prefixo R$, para preencher o input)
const numeroParaBRL = (n) => (n || n === 0)
  ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  : '';

const labelMes = (yyyymm) => {
  const [ano, mes] = yyyymm.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[parseInt(mes, 10) - 1]}/${ano.slice(2)}`;
};

const STATUS_ESTEIRA = {
  identificado: { label: 'Identificado', cor: 'secondary' },
  em_negociacao: { label: 'Em negociação', cor: 'primary' },
  realizado: { label: 'Realizado', cor: 'success' },
  perdido: { label: 'Perdido', cor: 'danger' },
};

const SavingCliente = () => {
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  const [dadosCliente, setDadosCliente] = useState(null);
  const [dados, setDados] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);
  const [totalEconomiaRealizada, setTotalEconomiaRealizada] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');

  // Filtros
  const [busca, setBusca] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [mostrarTodos, setMostrarTodos] = useState(false);

  // Modais
  const [modalHistorico, setModalHistorico] = useState(false);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [dadosHistorico, setDadosHistorico] = useState(null);
  const [modalEmail, setModalEmail] = useState(false);
  const [modoEmail, setModoEmail] = useState('aumento'); // 'aumento' | 'anual'
  const [emailValorAdiantado, setEmailValorAdiantado] = useState(null); // valor negociado p/ o e-mail anual
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState(null);
  const [modalAjuda, setModalAjuda] = useState(false);
  const [anualInputs, setAnualInputs] = useState({}); // { [oportunidadeId]: valorAnualNegociado }
  const [modalRealizar, setModalRealizar] = useState(false);
  const [realizarOpp, setRealizarOpp] = useState(null);
  const [realizarValor, setRealizarValor] = useState('');
  const [salvoAnualId, setSalvoAnualId] = useState(null); // id da oportunidade anual recém-salva (feedback inline)

  const authHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('access')}` } });

  const carregarTudo = useCallback(async () => {
    setCarregando(true);
    try {
      const [resCliente, resSaving, resOportunidades] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`, authHeaders()),
        axios.get(`${process.env.REACT_APP_API_BASE}/api/saving-fornecedores/`, { params: { cliente_id }, ...authHeaders() }),
        axios.get(`${process.env.REACT_APP_API_BASE}/api/saving-oportunidades/`, { params: { cliente_id }, ...authHeaders() }),
      ]);
      if (resCliente.data.success) setDadosCliente(resCliente.data.cliente);
      if (resSaving.data.success) setDados(resSaving.data);
      if (resOportunidades.data.success) {
        setOportunidades(resOportunidades.data.oportunidades);
        setTotalEconomiaRealizada(resOportunidades.data.total_economia_realizada);
      }
    } catch (error) {
      console.error('Erro ao carregar Saving:', error);
      if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro ao carregar dados do Saving');
      }
    } finally {
      setCarregando(false);
    }
  }, [cliente_id, navigate]);

  useEffect(() => { carregarTudo(); }, [carregarTudo]);
  // Padroniza as mensagens como toast (sucesso/atenção/erro)
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const abrirHistorico = async (forn) => {
    setFornecedorSelecionado(forn);
    setCarregandoHistorico(true);
    setDadosHistorico(null);
    setModalHistorico(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-fornecedor/`,
        { params: { cliente_id, codigo_fornecedor: forn.fornecedor_id }, ...authHeaders() }
      );
      if (res.data.success) {
        const d = res.data.dados;
        // E-mail salvo via saving_config tem precedência sobre o cadastro
        if (!d.fornecedor.email && forn.email) d.fornecedor.email = forn.email;
        setDadosHistorico(d);
      }
    } catch (e) {
      console.error('Erro ao buscar histórico:', e);
      setMensagem('Erro ao buscar histórico do fornecedor');
      setTimeout(() => setMensagem(''), 3000);
      setModalHistorico(false);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const salvarConfig = async (forn, acao, email) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/saving-config/`,
        { cliente_id, fornecedor_id: forn.fornecedor_id, acao, email_fornecedor: email },
        authHeaders()
      );
    } catch (e) {
      console.error('Erro ao salvar config:', e);
      setMensagem('Erro ao salvar configuração');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  const alternarRecorrente = async (forn) => {
    // incluir/excluir manual; voltar ao automático quando o toggle coincide com a detecção
    let novaAcao;
    if (forn.recorrente) {
      novaAcao = forn.recorrente_auto ? 'excluir' : null;
    } else {
      novaAcao = forn.recorrente_auto ? null : 'incluir';
    }
    await salvarConfig(forn, novaAcao, null);
    await carregarTudo();
  };

  const atualizarOportunidade = async (id, campos) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_BASE}/api/saving-oportunidades/`,
        { cliente_id, id, ...campos },
        authHeaders()
      );
      await carregarTudo();
    } catch (e) {
      console.error('Erro ao atualizar oportunidade:', e);
      setMensagem('Erro ao atualizar oportunidade');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  // Salva o valor anual negociado e dá feedback visível (toast + check inline no botão)
  const salvarValorAnual = async (o, negociado) => {
    await atualizarOportunidade(o.id, { valor_novo: negociado });
    setSalvoAnualId(o.id);
    setMensagem('Valor negociado salvo!');
    setTimeout(() => setMensagem(''), 2500);
    setTimeout(() => setSalvoAnualId(prev => (prev === o.id ? null : prev)), 2500);
  };

  // Abre o modal para registrar a economia ao concluir a negociação
  const abrirRealizar = (o) => {
    setRealizarOpp(o);
    // pré-preenche: anual usa o valor já negociado; aumento começa vazio
    if (o.tipo === 'anual') {
      setRealizarValor(numeroParaBRL(o.valor_novo) || '');
    } else {
      setRealizarValor('');
    }
    setModalRealizar(true);
  };

  const confirmarRealizado = async () => {
    if (!realizarOpp) return;
    const o = realizarOpp;

    if (o.tipo === 'anual') {
      const valorAcordado = parseBRL(realizarValor);
      if (valorAcordado <= 0) {
        setMensagem('Informe o valor anual acordado para registrar a economia.');
        setTimeout(() => setMensagem(''), 3000);
        return;
      }
      const ref = o.valor_anual_cheio || o.valor_referencia * 12;
      const obs = `Contrato anual fechado: de ${formatarValor(ref)} para ${formatarValor(valorAcordado)} (ganho de ${formatarValor(Math.max(0, ref - valorAcordado))}).`;
      await atualizarOportunidade(o.id, { status: 'realizado', valor_novo: valorAcordado, observacao: obs });
    } else {
      // Aumento: ganho = excesso mensal x meses restantes (automático, sem digitar)
      const meses = o.meses_restantes || 12;
      const ganho = (o.excesso_mensal || 0) * meses;
      const obs = `Excesso mensal de ${formatarValor(o.excesso_mensal || 0)} eliminado × ${meses} ${meses === 1 ? 'mês' : 'meses'} restantes = ganho de ${formatarValor(ganho)}.`;
      await atualizarOportunidade(o.id, { status: 'realizado', observacao: obs });
    }

    setModalRealizar(false);
    setRealizarOpp(null);
    setMensagem('Economia registrada como realizada!');
    setTimeout(() => setMensagem(''), 3000);
  };

  const deletarOportunidade = async (o) => {
    const nome = o.nome_fornecedor || o.fornecedor_id;
    const ok = window.confirm(
      `Atenção!\n\nDeseja realmente excluir esta negociação de "${nome}" da esteira?\n\nEsta ação não pode ser desfeita.`
    );
    if (!ok) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/api/saving-oportunidades/`,
        { data: { cliente_id, id: o.id }, ...authHeaders() }
      );
      setMensagem('Negociação excluída da esteira.');
      setTimeout(() => setMensagem(''), 3000);
      await carregarTudo();
    } catch (e) {
      console.error('Erro ao excluir oportunidade:', e);
      setMensagem('Erro ao excluir negociação');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  const criarOportunidade = async (forn, tipo = 'aumento') => {
    const jaAberta = oportunidades.some(
      o => o.fornecedor_id === forn.fornecedor_id && o.tipo === tipo && ['identificado', 'em_negociacao'].includes(o.status)
    );
    if (jaAberta) {
      setMensagem('Já existe uma oportunidade aberta deste tipo para este fornecedor.');
      setTimeout(() => setMensagem(''), 3000);
      return;
    }
    // Excesso mensal do reajuste = último mês com valor − primeiro mês com valor (Var. Acum em R$)
    const comValor = (forn.valores || []).filter(v => v > 0);
    const excessoMensal = comValor.length >= 2 ? Math.max(0, comValor[comValor.length - 1] - comValor[0]) : 0;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/saving-oportunidades/`,
        {
          cliente_id,
          fornecedor_id: forn.fornecedor_id,
          nome_fornecedor: forn.nome,
          tipo,
          valor_referencia: forn.media,
          excesso_mensal: tipo === 'aumento' ? excessoMensal : 0,
          economia_estimada_anual: 0,
        },
        authHeaders()
      );
      setMensagem(tipo === 'anual'
        ? 'Oportunidade de contrato anual registrada!'
        : 'Oportunidade registrada na esteira!');
      setTimeout(() => setMensagem(''), 3000);
      await carregarTudo();
    } catch (e) {
      console.error('Erro ao criar oportunidade:', e);
      setMensagem('Erro ao registrar oportunidade');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  // Abre o e-mail de uma oportunidade da esteira: busca histórico e abre o modal no modo indicado
  // modo: 'anual' (proposta anual), 'explicacao' (pedir explicação do aumento) ou 'aumento'
  const abrirEmailEsteira = async (fornecedorId, nome, modo = 'anual', valorAdiantado) => {
    setFornecedorSelecionado({ fornecedor_id: fornecedorId, nome });
    setEmailValorAdiantado(modo === 'anual' && valorAdiantado && valorAdiantado > 0 ? valorAdiantado : null);
    setCarregandoHistorico(true);
    setDadosHistorico(null);
    setModoEmail(modo);
    setModalEmail(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-fornecedor/`,
        { params: { cliente_id, codigo_fornecedor: fornecedorId }, ...authHeaders() }
      );
      if (res.data.success) setDadosHistorico(res.data.dados);
    } catch (e) {
      console.error('Erro ao buscar histórico p/ negociação anual:', e);
      setMensagem('Erro ao abrir negociação anual');
      setTimeout(() => setMensagem(''), 3000);
      setModalEmail(false);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  if (carregando && !dados) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5 text-center">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3 text-muted">Carregando Saving...</p>
        </div>
      </div>
    );
  }

  const fornecedores = (dados?.fornecedores || [])
    .filter(f => mostrarTodos || f.recorrente)
    .filter(f => filtroNivel === 'todos' || f.nivel === filtroNivel)
    .filter(f => filtroTipo === 'todos' || f.tipo === filtroTipo)
    .filter(f => !busca || (f.nome || '').toLowerCase().includes(busca.toLowerCase()))
    // Ordena do maior aumento (Var. Acum.) para o menor; sem variação vai para o fim
    .sort((a, b) => {
      const va = (a.variacao_acumulada === null || a.variacao_acumulada === undefined) ? -Infinity : a.variacao_acumulada;
      const vb = (b.variacao_acumulada === null || b.variacao_acumulada === undefined) ? -Infinity : b.variacao_acumulada;
      return vb - va;
    });

  // Total das médias dos recorrentes — base para o % que cada pagamento representa no mês
  const totalMediasRecorrentes = (dados?.fornecedores || [])
    .filter(f => f.recorrente)
    .reduce((s, f) => s + (f.media || 0), 0);

  const janela = dados?.janela || [];
  const cards = dados?.cards || {};
  const oportunidadesAumento = oportunidades.filter(o => (o.tipo || 'aumento') === 'aumento');
  const oportunidadesAnual = oportunidades.filter(o => o.tipo === 'anual');
  // Mapa fornecedor_id -> oportunidades (exibe o status na coluna "Negociação", inclusive 'perdido')
  const negociacaoPorFornecedor = oportunidades.reduce((acc, o) => {
    (acc[o.fornecedor_id] = acc[o.fornecedor_id] || []).push({ status: o.status, tipo: o.tipo || 'aumento' });
    return acc;
  }, {});

  return (
    <div className="das-page fmp-brand-page">
      <div className="container-fluid mt-4">

        {/* HEADER */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Financial Management Platform
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-piggy-bank header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">
                        Saving — Controle de Pagamentos {dadosCliente ? `- ${dadosCliente.nome}` : ''}
                      </h2>
                    </div>
                    {dadosCliente && (
                      <p className="mb-1 opacity-75" style={{ color: '#000' }}>
                        <i className="fas fa-building me-2"></i>
                        CNPJ: {dadosCliente.cnpj}
                      </p>
                    )}
                    <p className="mb-0 opacity-75" style={{ color: '#000' }}>
                      <i className="fas fa-calendar me-2"></i>
                      Janela: {janela.length > 0 && `${labelMes(janela[0])} a ${labelMes(janela[janela.length - 1])} (mês atual parcial)`}
                    </p>
                  </div>
                  <div className="mb-2 d-flex flex-column gap-1">
                    <button
                      className="btn btn-sm"
                      onClick={() => setModalAjuda(true)}
                      title="Entenda como usar esta página"
                      style={{ backgroundColor: '#fff', borderColor: '#00205b', color: '#00205b', fontSize: '0.72rem', padding: '3px 8px', fontWeight: 600 }}
                    >
                      <i className="fas fa-circle-question me-1"></i>Como usar
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate(
                        dadosCliente?.sistema?.toLowerCase() === 'conta_azul'
                          ? `/dashboard-conta-azul/${cliente_id}`
                          : `/dashboard-cliente/${cliente_id}`
                      )}
                      title="Voltar ao Dashboard"
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff', fontSize: '0.72rem', padding: '3px 8px' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar ao Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* CARDS RESUMO */}
        <div className="row mb-4">
          {[
            { titulo: 'Total Recorrente (mês)', valor: formatarValor(cards.total_recorrente_mes), icone: 'fa-sync-alt', cor: 'text-primary', sub: 'Soma das médias mensais' },
            { titulo: 'Fornecedores Monitorados', valor: cards.fornecedores_monitorados ?? 0, icone: 'fa-users', cor: 'text-dark', sub: 'Recorrentes na janela' },
            { titulo: 'Economia Potencial', valor: formatarValor(cards.economia_potencial), icone: 'fa-bullseye', cor: 'text-danger', sub: 'Total das diferenças apontadas (resto do ano)' },
            { titulo: 'Economia Realizada', valor: formatarValor(totalEconomiaRealizada), icone: 'fa-piggy-bank', cor: 'text-success', sub: 'Esteira de savings' },
          ].map((c, i) => (
            <div key={i} className="col mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className={`${c.cor} me-2`}><i className={`fas ${c.icone} fa-2x`}></i></div>
                    <h6 className="text-muted mb-0">{c.titulo}</h6>
                  </div>
                  <h4 className={`${c.cor} mb-0`}>{c.valor}</h4>
                  <small className="text-muted">{c.sub}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FILTROS */}
        <div className="row mb-3 g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label fw-bold"><i className="fas fa-search me-2"></i>Buscar fornecedor</label>
            <input type="text" className="form-control" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome do fornecedor..." />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold">Nível</label>
            <select className="form-select" value={filtroNivel} onChange={(e) => setFiltroNivel(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="estavel">Estável</option>
              <option value="atencao">Atenção</option>
              <option value="critico">Crítico</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold">Tipo de conta</label>
            <select className="form-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="fixa">Fixa (contrato)</option>
              <option value="variavel">Variável (consumo)</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="form-check form-switch mt-4">
              <input className="form-check-input" type="checkbox" id="mostrarTodos" checked={mostrarTodos} onChange={(e) => setMostrarTodos(e.target.checked)} />
              <label className="form-check-label" htmlFor="mostrarTodos">Mostrar não-recorrentes</label>
            </div>
          </div>
          <div className="col-md-2 text-end">
            <small className="text-muted">{fornecedores.length} fornecedor{fornecedores.length !== 1 ? 'es' : ''}</small>
          </div>
        </div>

        {/* TABELA PRINCIPAL */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Fornecedor</th>
                        {janela.map((m, i) => (
                          <th key={m} className="text-end">
                            {labelMes(m)}{i === janela.length - 1 ? ' *' : ''}
                          </th>
                        ))}
                        <th className="text-end">Média</th>
                        <th className="text-end">Var. Acum.</th>
                        <th className="text-end">YoY</th>
                        <th className="text-end">Proj. Anual</th>
                        <th className="text-center">Negociação</th>
                        <th className="text-center">Recorrente</th>
                        <th className="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fornecedores.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="text-center py-5 text-muted">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            Nenhum fornecedor encontrado com os filtros atuais.
                          </td>
                        </tr>
                      ) : fornecedores.map((f) => (
                        <tr key={f.fornecedor_id}>
                          <td>
                            <strong style={{ color: '#00205b', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px', textDecorationColor: 'rgba(0,32,91,0.35)' }} onClick={() => abrirHistorico(f)} title="Ver todos os pagamentos">
                              {f.nome}
                            </strong>
                            <div className="d-flex gap-1 mt-1 flex-wrap">
                              <span className={`badge ${f.tipo === 'fixa' ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '10px' }}>
                                {f.tipo === 'fixa' ? 'Fixa' : 'Variável'}
                              </span>
                              {totalMediasRecorrentes > 0 && f.media > 0 && (
                                <span className="badge bg-dark" style={{ fontSize: '10px' }} title="Quanto este pagamento representa do total recorrente do mês">
                                  {((f.media / totalMediasRecorrentes) * 100).toFixed(1)}% do mês
                                </span>
                              )}
                              {f.novo && <span className="badge bg-success" style={{ fontSize: '10px' }}>Novo</span>}
                              {f.sem_pagamento_mes && <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }}>Sem pagto no mês</span>}
                              {f.override && <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }} title="Ajuste manual de recorrência">Manual</span>}
                            </div>
                          </td>
                          {f.valores.map((v, i) => (
                            <td key={i} className="text-end">{v > 0 ? formatarValor(v) : <span className="text-muted">—</span>}</td>
                          ))}
                          <td className="text-end"><strong>{formatarValor(f.media)}</strong></td>
                          <td className="text-end">
                            {(f.variacao_acumulada !== null && f.variacao_acumulada !== undefined) ? (() => {
                              const comValor = (f.valores || []).filter(v => v > 0);
                              const diff = comValor.length >= 2 ? comValor[comValor.length - 1] - comValor[0] : 0;
                              const cor = diff > 0 ? '#e74c3c' : diff < 0 ? '#27ae60' : '#7f8c8d';
                              const seta = diff > 0 ? '▲' : diff < 0 ? '▼' : '';
                              const label = diff > 0 ? 'Aumentou' : diff < 0 ? 'Reduziu' : 'Estável';
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                  <div style={{ color: cor, fontWeight: 700, fontSize: '15px' }}>{seta} {formatarValor(Math.abs(diff))}</div>
                                  <small style={{ color: cor, fontSize: '10px', fontWeight: 600 }}>{label} {formatarPct(f.variacao_acumulada)}</small>
                                </div>
                              );
                            })() : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end text-muted">{formatarPct(f.yoy_pct)}</td>
                          <td className="text-end">
                            {formatarValor(f.projecao_anual)}
                            {f.impacto_anual > 0 && (
                              <div><small className="text-danger">+{formatarValor(f.impacto_anual)}/ano</small></div>
                            )}
                          </td>
                          <td className="text-center">
                            {(() => {
                              const ops = negociacaoPorFornecedor[f.fornecedor_id] || [];
                              if (ops.length === 0) return <span className="text-muted">—</span>;
                              return (
                                <div className="d-flex flex-column align-items-center gap-1">
                                  {ops.map((op, i) => (
                                    <span key={i} className={`badge bg-${STATUS_ESTEIRA[op.status].cor}`} title={op.tipo === 'anual' ? 'Contrato anual' : 'Renegociação por aumento'}>
                                      <i className={`fas ${op.tipo === 'anual' ? 'fa-calendar-check' : 'fa-piggy-bank'} me-1`}></i>
                                      {STATUS_ESTEIRA[op.status].label}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="text-center">
                            <div className="form-check form-switch d-inline-block">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={f.recorrente}
                                onChange={() => alternarRecorrente(f)}
                                title={f.recorrente ? 'Excluir do monitoramento' : 'Incluir no monitoramento'}
                              />
                            </div>
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirHistorico(f)} title="Ver pagamentos">
                              <i className="fas fa-list-ul"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-success me-1" onClick={() => criarOportunidade(f, 'aumento')} title="Registrar oportunidade de renegociação (por aumento)">
                              <i className="fas fa-piggy-bank"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-dark" onClick={() => criarOportunidade(f, 'anual')} title="Propor contrato anual adiantado">
                              <i className="fas fa-calendar-check"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-3 py-2 border-top">
                  <small className="text-muted">* Mês atual parcial — valores ainda em consolidação.</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ESTEIRA 1 — RENEGOCIAÇÃO POR AUMENTO */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-funnel-dollar me-2 text-danger"></i>
                  Esteira — Renegociação por Aumento
                </h5>
                <span className="badge bg-success">{formatarValor(totalEconomiaRealizada)} economizados (total)</span>
              </div>
              <div className="card-body p-0">
                {oportunidadesAumento.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                    Nenhuma oportunidade de renegociação. Use o botão <i className="fas fa-piggy-bank"></i> na tabela ou envie um e-mail de negociação.
                  </div>
                ) : (
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Fornecedor</th>
                        <th>Status</th>
                        <th className="text-end">Valor Referência</th>
                        <th className="text-end">Valor Excedido</th>
                        <th className="text-end">Economia estimada (resto do ano)</th>
                        <th className="text-end">Economia Realizada</th>
                        <th>Identificada em</th>
                        <th className="text-center">Mover para</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oportunidadesAumento.map((o) => (
                        <tr key={o.id}>
                          <td><strong>{o.nome_fornecedor || o.fornecedor_id}</strong></td>
                          <td><span className={`badge bg-${STATUS_ESTEIRA[o.status].cor}`}>{STATUS_ESTEIRA[o.status].label}</span></td>
                          <td className="text-end">{formatarValor(o.valor_referencia)}</td>
                          <td className="text-end">{o.excesso_mensal > 0 ? <strong className="text-danger">▲ {formatarValor(o.excesso_mensal)}</strong> : '—'}</td>
                          <td className="text-end">{o.economia_estimada_anual ? formatarValor(o.economia_estimada_anual) : '—'}</td>
                          <td className="text-end">
                            {o.status === 'realizado'
                              ? <strong className="text-success">{formatarValor(o.economia_realizada)}</strong>
                              : '—'}
                          </td>
                          <td>{o.mes_referencia}</td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            {(o.status === 'identificado' || o.status === 'em_negociacao') && (
                              <button className="btn btn-sm btn-outline-dark me-1" onClick={() => abrirEmailEsteira(o.fornecedor_id, o.nome_fornecedor, 'explicacao')} title="Enviar e-mail pedindo explicação do aumento">
                                <i className="fas fa-envelope me-1"></i>Negociar
                              </button>
                            )}
                            {o.status === 'identificado' && (
                              <button className="btn btn-sm btn-outline-primary me-1" onClick={() => atualizarOportunidade(o.id, { status: 'em_negociacao' })}>
                                Em negociação
                              </button>
                            )}
                            {(o.status === 'identificado' || o.status === 'em_negociacao') && (
                              <>
                                <button className="btn btn-sm btn-outline-success me-1" onClick={() => abrirRealizar(o)}>
                                  Realizado
                                </button>
                                <button className="btn btn-sm btn-outline-danger me-1" onClick={() => atualizarOportunidade(o.id, { status: 'perdido' })}>
                                  Perdido
                                </button>
                              </>
                            )}
                            <button className="btn btn-sm btn-outline-danger" title="Excluir negociação da esteira" onClick={() => deletarOportunidade(o)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ESTEIRA 2 — CONTRATO ANUAL ADIANTADO */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-calendar-check me-2 text-dark"></i>
                  Esteira — Contrato Anual Adiantado
                </h5>
                <small className="text-muted">Pague o ano adiantado e negocie desconto</small>
              </div>
              <div className="card-body p-0">
                {oportunidadesAnual.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                    Nenhuma proposta de contrato anual. Use o botão <i className="fas fa-calendar-check"></i> na tabela para propor a um fornecedor.
                  </div>
                ) : (
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Fornecedor</th>
                        <th>Status</th>
                        <th className="text-end">Mensal</th>
                        <th className="text-end">Total até dez.</th>
                        <th className="text-end">Valor Anual Negociado</th>
                        <th className="text-end">Economia</th>
                        <th className="text-center">Mover para</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oportunidadesAnual.map((o) => {
                        const mesesRestantes = o.meses_restantes || 12;
                        const anualCheio = o.valor_anual_cheio || (o.valor_referencia * mesesRestantes);
                        const inputVal = anualInputs[o.id] !== undefined ? anualInputs[o.id] : numeroParaBRL(o.valor_novo);
                        const negociado = parseBRL(inputVal);
                        const economiaPrevista = negociado > 0 ? anualCheio - negociado : (o.economia_estimada_anual || 0);
                        return (
                          <tr key={o.id}>
                            <td><strong>{o.nome_fornecedor || o.fornecedor_id}</strong></td>
                            <td><span className={`badge bg-${STATUS_ESTEIRA[o.status].cor}`}>{STATUS_ESTEIRA[o.status].label}</span></td>
                            <td className="text-end">{formatarValor(o.valor_referencia)}</td>
                            <td className="text-end">
                              {formatarValor(anualCheio)}
                              <div><small className="text-muted">{mesesRestantes} {mesesRestantes === 1 ? 'mês' : 'meses'} até dez.</small></div>
                            </td>
                            <td className="text-end">
                              <div className="input-group input-group-sm" style={{ maxWidth: '200px', marginLeft: 'auto' }}>
                                <span className="input-group-text">R$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control text-end"
                                  value={inputVal}
                                  placeholder="0,00"
                                  onChange={(e) => { setAnualInputs(prev => ({ ...prev, [o.id]: formatarInputBRL(e.target.value) })); if (salvoAnualId === o.id) setSalvoAnualId(null); }}
                                />
                                <button
                                  className={salvoAnualId === o.id ? 'btn btn-success' : 'btn btn-outline-secondary'}
                                  title="Salvar valor anual negociado"
                                  onClick={() => salvarValorAnual(o, negociado)}
                                >
                                  <i className={`fas ${salvoAnualId === o.id ? 'fa-check' : 'fa-save'}`}></i>
                                  {salvoAnualId === o.id ? ' Salvo' : ''}
                                </button>
                              </div>
                            </td>
                            <td className="text-end">
                              {economiaPrevista > 0
                                ? <strong className={o.status === 'realizado' ? 'text-success' : 'text-primary'}>{formatarValor(economiaPrevista)}</strong>
                                : '—'}
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {(o.status === 'identificado' || o.status === 'em_negociacao') && (
                                <button className="btn btn-sm btn-outline-dark me-1" onClick={() => abrirEmailEsteira(o.fornecedor_id, o.nome_fornecedor, 'anual', negociado || o.valor_novo)} title="Enviar e-mail de proposta anual">
                                  <i className="fas fa-envelope me-1"></i>Negociar
                                </button>
                              )}
                              {o.status === 'identificado' && (
                                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => atualizarOportunidade(o.id, { status: 'em_negociacao' })}>
                                  Em negociação
                                </button>
                              )}
                              {(o.status === 'identificado' || o.status === 'em_negociacao') && (
                                <>
                                  <button className="btn btn-sm btn-outline-success me-1" onClick={() => atualizarOportunidade(o.id, { status: 'realizado' })}>
                                    Realizado
                                  </button>
                                  <button className="btn btn-sm btn-outline-danger me-1" onClick={() => atualizarOportunidade(o.id, { status: 'perdido' })}>
                                    Perdido
                                  </button>
                                </>
                              )}
                              <button className="btn btn-sm btn-outline-danger" title="Excluir negociação da esteira" onClick={() => deletarOportunidade(o)}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MODAIS COMPARTILHADOS */}
        <ModalHistoricoFornecedor
          aberto={modalHistorico}
          carregando={carregandoHistorico}
          dados={dadosHistorico}
          onFechar={() => setModalHistorico(false)}
          onNegociar={() => { setModoEmail('aumento'); setEmailValorAdiantado(null); setModalEmail(true); }}
        />
        <ModalEmailNegociacao
          aberto={modalEmail}
          tipo={modoEmail}
          valorAdiantadoSugerido={emailValorAdiantado}
          dadosHistorico={dadosHistorico}
          clienteId={cliente_id}
          nomeRemetente={dadosCliente?.nome}
          onFechar={() => { setModalEmail(false); carregarTudo(); }}
          onMensagem={setMensagem}
          onEmailDefinido={(email) => fornecedorSelecionado && salvarConfig(fornecedorSelecionado, null, email)}
        />

        {/* MODAL REGISTRAR ECONOMIA REALIZADA */}
        {modalRealizar && realizarOpp && (() => {
          const o = realizarOpp;
          const ehAnual = o.tipo === 'anual';
          const meses = o.meses_restantes || 12;
          // Anual: ganho = total até dez − valor acordado | Aumento: excesso mensal × meses restantes
          const refAnual = o.valor_anual_cheio || o.valor_referencia * 12;
          const acordado = parseBRL(realizarValor);
          const ganho = ehAnual
            ? Math.max(0, refAnual - acordado)
            : (o.excesso_mensal || 0) * meses;
          const podeConfirmar = ehAnual ? acordado > 0 : (o.excesso_mensal || 0) > 0;
          return (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1095 }} onClick={() => setModalRealizar(false)}>
              <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-circle-check me-2"></i>
                      Registrar economia realizada
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setModalRealizar(false)}></button>
                  </div>
                  <div className="modal-body">
                    <p className="mb-3">
                      <strong>{o.nome_fornecedor || o.fornecedor_id}</strong>
                      <span className="badge bg-dark ms-2">
                        <i className={`fas ${ehAnual ? 'fa-calendar-check' : 'fa-piggy-bank'} me-1`}></i>
                        {ehAnual ? 'Contrato anual' : 'Renegociação por aumento'}
                      </span>
                    </p>

                    {ehAnual ? (
                      <>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Total até dezembro (referência):</span>
                          <strong>{formatarValor(refAnual)}</strong>
                        </div>
                        <label className="form-label fw-bold mt-2">Valor anual acordado</label>
                        <div className="input-group mb-1">
                          <span className="input-group-text">R$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={realizarValor}
                            placeholder="0,00"
                            autoFocus
                            onChange={(e) => setRealizarValor(formatarInputBRL(e.target.value))}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Excesso mensal (reajuste):</span>
                          <strong className="text-danger">{formatarValor(o.excesso_mensal || 0)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Meses restantes até dezembro:</span>
                          <strong>{meses}</strong>
                        </div>
                        <p className="text-muted small mb-0">
                          Ao concluir, o excesso de {formatarValor(o.excesso_mensal || 0)}/mês deixa de ser pago pelos {meses} {meses === 1 ? 'mês' : 'meses'} restantes — esse é o ganho.
                        </p>
                      </>
                    )}

                    <div className="card border-0 mt-3" style={{ backgroundColor: '#e8f5e9' }}>
                      <div className="card-body py-3 text-center">
                        <div className="text-muted small">Ganho registrado</div>
                        <div style={{ color: '#1b5e20', fontWeight: 700, fontSize: '22px' }}>
                          {formatarValor(ganho)}
                        </div>
                        {!ehAnual && (
                          <small className="text-muted">{formatarValor(o.excesso_mensal || 0)}/mês × {meses} {meses === 1 ? 'mês' : 'meses'}</small>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setModalRealizar(false)}>Cancelar</button>
                    <button className="btn btn-success" onClick={confirmarRealizado} disabled={!podeConfirmar}>
                      <i className="fas fa-check me-2"></i>Confirmar economia
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* MODAL AJUDA / COMO USAR */}
        {modalAjuda && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1085 }}
            onClick={() => setModalAjuda(false)}
          >
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '85vw' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-circle-question me-2"></i>
                      Como usar o Saving — Controle de Pagamentos
                    </h5>
                    <small style={{ color: '#6c757d' }}>Entenda cada coluna, selo e ação desta página</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setModalAjuda(false)}></button>
                </div>

                <div className="modal-body" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  {/* O que é */}
                  <div className="alert alert-light border" role="alert">
                    <strong><i className="fas fa-piggy-bank me-2 text-primary"></i>O que é esta página?</strong>
                    <p className="mb-0 mt-1">
                      O Saving monitora os pagamentos aos seus <strong>fornecedores recorrentes</strong> (aluguel, CLT, PJ,
                      software etc.) comparando os valores dos <strong>últimos 5 meses</strong>. Ele destaca quem aumentou,
                      calcula em quanto isso impacta no ano e ajuda você a negociar — tudo a partir dos pagamentos que já
                      estão no Omie / Conta Azul. Só entram valores <strong>efetivamente pagos</strong>.
                    </p>
                  </div>

                  {/* Cards */}
                  <h6 className="fw-bold mt-4 mb-2"><i className="fas fa-layer-group me-2 text-primary"></i>Cards do topo</h6>
                  <ul className="mb-0">
                    <li><strong>Total Recorrente (mês)</strong> — soma das médias mensais dos fornecedores recorrentes (valor médio que sai por mês).</li>
                    <li><strong>Fornecedores Monitorados</strong> — quantos fornecedores recorrentes estão sendo acompanhados.</li>
                    <li><strong>Economia Potencial</strong> — quanto, no ano, você pode economizar negociando os fornecedores que aumentaram.</li>
                    <li><strong>Economia Realizada</strong> — quanto já foi efetivamente economizado nas negociações concluídas (vem da esteira).</li>
                  </ul>

                  {/* Colunas */}
                  <h6 className="fw-bold mt-4 mb-2"><i className="fas fa-table-columns me-2 text-primary"></i>Colunas da tabela</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <tbody>
                        <tr><td className="fw-bold" style={{ width: '32%' }}>Fornecedor</td><td>Nome do fornecedor. Clique para ver todo o histórico de pagamentos. Abaixo do nome aparecem os selos (explicados adiante).</td></tr>
                        <tr><td className="fw-bold">Colunas dos meses *</td><td>Total pago ao fornecedor em cada um dos 5 meses. O <strong>*</strong> marca o mês atual, ainda <strong>parcial</strong>. "—" = não houve pagamento naquele mês.</td></tr>
                        <tr><td className="fw-bold">Média</td><td>Média mensal dos meses fechados (não inclui o mês parcial). É a referência para projeção e impacto.</td></tr>
                        <tr><td className="fw-bold">Var. Acum.</td><td>Variação do primeiro ao último mês com valor na janela, pegando o aumento gradual. Mostra a seta <span style={{ color: '#e74c3c' }}>▲ vermelha</span> quando aumentou e <span style={{ color: '#27ae60' }}>▼ verde</span> quando reduziu, com o valor em R$ e o percentual.</td></tr>
                        <tr><td className="fw-bold">YoY</td><td>Compara o mês atual com o <strong>mesmo mês do ano passado</strong>. Ajuda a separar aumento real de sazonalidade (13º, férias, conta de luz no verão).</td></tr>
                        <tr><td className="fw-bold">Proj. Anual</td><td>Quanto o fornecedor custará no ano se o ritmo continuar (média × 12). Quando há aumento, mostra em vermelho o impacto anual desse aumento.</td></tr>
                        <tr><td className="fw-bold">Negociação</td><td>Mostra se o fornecedor tem alguma oportunidade aberta nas esteiras e em que status está (ex.: <span className="badge bg-primary">Em negociação</span>). O ícone indica o tipo: <i className="fas fa-piggy-bank"></i> renegociação por aumento, <i className="fas fa-calendar-check"></i> contrato anual.</td></tr>
                        <tr><td className="fw-bold">Recorrente</td><td>Interruptor que liga/desliga o monitoramento. Vem ligado automaticamente para quem pagou em pelo menos 3 dos 5 meses; você pode incluir/excluir na mão.</td></tr>
                        <tr><td className="fw-bold">Ações</td><td>Ver pagamentos (histórico + análise) e registrar uma oportunidade de economia () na esteira.</td></tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Selos */}
                  <h6 className="fw-bold mt-4 mb-2"><i className="fas fa-tags me-2 text-primary"></i>Selos abaixo do nome</h6>
                  <ul className="mb-0">
                    <li><span className="badge bg-info me-1">Fixa</span> conta de contrato (aluguel, PJ, software): valor estável — qualquer variação é suspeita.</li>
                    <li><span className="badge bg-secondary me-1">Variável</span> conta que oscila naturalmente (energia, frete): o alerta é mais tolerante.</li>
                    <li><span className="badge bg-dark me-1">X% do mês</span> quanto o pagamento desse fornecedor representa do total recorrente do mês — quanto maior, mais peso ele tem no seu caixa.</li>
                    <li><span className="badge bg-success me-1">Novo</span> primeiro mês que o fornecedor aparece (sem histórico para comparar).</li>
                    <li><span className="badge bg-warning text-dark me-1">Sem pagto no mês</span> é recorrente mas não tem pagamento no mês atual (conta não paga, contrato encerrado, etc.).</li>
                    <li><span className="badge bg-light text-dark border me-1">Manual</span> a recorrência foi ajustada por você na mão, em vez da detecção automática.</li>
                  </ul>

                  {/* Esteira */}
                  <h6 className="fw-bold mt-4 mb-2"><i className="fas fa-funnel-dollar me-2 text-success"></i>As duas esteiras de Savings</h6>
                  <p className="mb-1">Há dois tipos de oportunidade, cada uma na sua esteira. Na tabela, cada fornecedor tem dois botões: <i className="fas fa-piggy-bank"></i> (renegociação por aumento) e <i className="fas fa-calendar-check"></i> (propor contrato anual).</p>
                  <ul className="mb-2">
                    <li><strong>Renegociação por Aumento</strong> — quando o fornecedor está cobrando mais; o objetivo é reduzir o valor. A economia é medida automaticamente nos meses seguintes (se passar a pagar menos).</li>
                    <li><strong>Contrato Anual Adiantado</strong> — propor pagar o ano inteiro adiantado em troca de desconto. Você informa o <strong>valor anual negociado</strong> e o sistema calcula a economia = (mensal × 12) − valor negociado.</li>
                  </ul>
                  <p className="mb-1">Em ambas, a oportunidade passa pelos status:</p>
                  <ul className="mb-0">
                    <li><span className="badge bg-secondary me-1">Identificado</span> registrada, ainda não trabalhada.</li>
                    <li><span className="badge bg-primary me-1">Em negociação</span> conversa em andamento (o envio do e-mail marca isso automaticamente).</li>
                    <li><span className="badge bg-success me-1">Realizado</span> negociação fechada — a economia é contabilizada.</li>
                    <li><span className="badge bg-danger me-1">Perdido</span> não foi possível negociar.</li>
                  </ul>

                  {/* Fluxo sugerido */}
                  <div className="alert alert-success border mt-4 mb-0">
                    <strong><i className="fas fa-route me-2"></i>Passo a passo sugerido</strong>
                    <ol className="mb-0 mt-1">
                      <li>Filtre por <strong>Crítico</strong> ou olhe os fornecedores de maior <strong>% do mês</strong> que aumentaram.</li>
                      <li>Clique no fornecedor para ver o histórico e a análise com o valor sugerido.</li>
                      <li>Use <strong>Negociar</strong> para enviar o e-mail — ele já entra na esteira como "Em negociação".</li>
                      <li>Fechou acordo? Marque como <strong>Realizado</strong> e acompanhe a Economia Realizada crescer.</li>
                    </ol>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setModalAjuda(false)}>
                    <i className="fas fa-check me-2"></i>Entendi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SavingCliente;
